import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function AddTenantModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    emergency_contact: '',
    emergency_phone: '',
    employer: '',
    property_id: '',
    weekly_rent: '',
    bond_amount: '',
    lease_start_date: '',
    lease_end_date: '',
    rent_frequency: 'weekly'
  });

  useEffect(() => {
    if (open) {
      loadProperties();
    }
  }, [open]);

  const loadProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Show both available and leased properties (can add multiple tenants to one property)
      setProperties(response.data.filter(p => p.status === 'available' || p.status === 'leased'));
    } catch (error) {
      console.error('Failed to load properties:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id) {
      toast.error('Please select a property');
      return;
    }
    
    if (!formData.weekly_rent || !formData.lease_start_date || !formData.lease_end_date) {
      toast.error('Please fill in all lease details');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Generate temporary password
      const tempPassword = 'Temp' + Math.random().toString(36).slice(2, 10) + '!';
      
      // Create the tenant with lease using the new endpoint
      const response = await axios.post(`${API}/tenants/create-with-lease`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        emergency_contact: formData.emergency_contact,
        emergency_phone: formData.emergency_phone,
        employer: formData.employer,
        password: tempPassword,
        property_id: formData.property_id,
        weekly_rent: parseFloat(formData.weekly_rent),
        bond_amount: parseFloat(formData.bond_amount || 0),
        lease_start_date: formData.lease_start_date,
        lease_end_date: formData.lease_end_date,
        rent_frequency: formData.rent_frequency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tenant created successfully! Welcome email sent with login credentials.');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        emergency_contact: '',
        emergency_phone: '',
        employer: '',
        property_id: '',
        weekly_rent: '',
        bond_amount: '',
        lease_start_date: '',
        lease_end_date: '',
        rent_frequency: 'weekly'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Tenant</DialogTitle>
          <DialogDescription>Create tenant account and profile</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Personal Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0400 123 456"
                />
              </div>
              <div>
                <Label>Employer</Label>
                <Input
                  value={formData.employer}
                  onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                  placeholder="Company Name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <Label>Emergency Contact</Label>
                <Input
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <Label>Emergency Phone</Label>
                <Input
                  type="tel"
                  value={formData.emergency_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                  placeholder="0400 987 654"
                />
              </div>
            </div>
          </div>

          {/* Property & Lease Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Property & Lease Details</h3>
            
            {properties.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3">
                <p className="text-sm text-yellow-900 font-semibold mb-2">⚠️ No Available Properties</p>
                <p className="text-sm text-yellow-800">
                  You need to create a property first before adding tenants. 
                  Go to <strong>Properties</strong> page and click <strong>"Add Property"</strong>.
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  <strong>Tip:</strong> Make sure the property status is "available" (not already leased).
                </p>
              </div>
            ) : (
              <div className="mb-3">
                <Label>Select Property *</Label>
                <Select value={formData.property_id} onValueChange={(value) => setFormData({ ...formData, property_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a property..." />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.address}, {property.suburb} 
                        {property.status === 'leased' && ' (Already leased - adding roommate)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {properties.length} propert{properties.length !== 1 ? 'ies' : 'y'} available 
                  ({properties.filter(p => p.status === 'leased').length} already leased)
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                  <p className="text-xs text-blue-800">
                    💡 <strong>Multiple tenants per property:</strong> You can add multiple tenants to the same property. If the property is already leased, this tenant will be added as a roommate to the existing lease.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Weekly Rent Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.weekly_rent}
                  onChange={(e) => setFormData({ ...formData, weekly_rent: e.target.value })}
                  placeholder="500.00"
                  required
                />
              </div>
              <div>
                <Label>Bond Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.bond_amount}
                  onChange={(e) => setFormData({ ...formData, bond_amount: e.target.value })}
                  placeholder="2000.00"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <Label>Lease Start Date *</Label>
                <Input
                  type="date"
                  value={formData.lease_start_date}
                  onChange={(e) => setFormData({ ...formData, lease_start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Lease End Date *</Label>
                <Input
                  type="date"
                  value={formData.lease_end_date}
                  onChange={(e) => setFormData({ ...formData, lease_end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="mt-3">
              <Label>Rent Frequency</Label>
              <Select value={formData.rent_frequency} onValueChange={(value) => setFormData({ ...formData, rent_frequency: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-900">
              <strong>📧 Email Notification:</strong> A welcome email with login credentials will be automatically sent to the tenant's email address.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || properties.length === 0}>
              {loading ? 'Creating...' : properties.length === 0 ? 'Add Property First' : 'Create Tenant & Send Welcome Email'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}